import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Link as MuiLink
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Phone
} from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useLogin, usePhoneLogin } from '@/hooks/useAuth';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import { GoogleSignInButton, PhoneAuthFlow, SMSVerificationModal } from '@/components/common';
import ReCaptcha from '@/components/common/ReCaptcha';
import { validatePhoneNumber } from '@/utils/phone-validation';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { recaptchaService } from '@/utils/recaptcha-service';

interface LoginForm {
  email: string;
  password: string;
}

interface PhoneLoginForm {
  phoneNumber: string;
  password: string;
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const emailLoginMutation = useLogin();
  const phoneLoginMutation = usePhoneLogin();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [showPhoneFlow, setShowPhoneFlow] = useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string>('');
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);

  const recaptcha = useRecaptcha({
    onError: (error) => setError(`reCAPTCHA error: ${error}`),
    onExpired: () => setError('reCAPTCHA has expired. Please verify again.'),
  });

  const returnUrl = searchParams.get('returnUrl') || '/dashboard';

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors }
  } = useForm<LoginForm>({ mode: 'onChange' });

  const {
    register: registerPhone,
    handleSubmit: handlePhoneSubmit,
    formState: { errors: phoneErrors }
  } = useForm<PhoneLoginForm>({ mode: 'onChange' });

  // Handle OAuth error from URL parameters
  useEffect(() => {
    const errorDescription = searchParams.get('error_description');
    if (errorDescription) {
      if (errorDescription.includes('ACCOUNT_EXISTS')) {
        // Extract email from error message if available
        const emailMatch = errorDescription.match(/email ([^\s]+)/);
        const email = emailMatch ? emailMatch[1] : '';

        setError(`An account with${email ? ` email ${email}` : ' this email'} already exists. Please try signing in below.`);
      } else {
        setError(errorDescription);
      }

      // Clear the error from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error_description');
      newSearchParams.delete('error');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);

  // Load reCAPTCHA configuration
  useEffect(() => {
    loadRecaptchaConfig();
  }, []);

  const loadRecaptchaConfig = () => {
    try {
      const enabled = recaptchaService.isEnabled();
      setRecaptchaEnabled(enabled);

      if (enabled) {
        const siteKey = recaptchaService.getSiteKey();
        setRecaptchaSiteKey(siteKey);
      }
    } catch (error) {
      console.warn('Could not load reCAPTCHA config:', error);
      setRecaptchaEnabled(false);
    }
  };

  const onEmailSubmit = async (data: LoginForm) => {
    setError(null);

    // Check reCAPTCHA if enabled
    if (recaptchaEnabled && !recaptcha.isVerified) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    try {
      const submitData = {
        ...data,
        recaptchaToken: recaptcha.token,
      };
      await emailLoginMutation.mutateAsync(submitData);
    } catch (err: any) {
      console.error('Email login error:', err);
      setError(err.message || 'Login failed');

      // Reset reCAPTCHA on error
      if (recaptchaEnabled) {
        recaptcha.reset();
      }
    }
  };

  const onPhoneSubmit = async (data: PhoneLoginForm) => {
    setError(null);

    // Check reCAPTCHA if enabled
    if (recaptchaEnabled && !recaptcha.isVerified) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    const phoneValidation = validatePhoneNumber(data.phoneNumber);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.error || 'Invalid phone number format');
      return;
    }

    try {
      const submitData = {
        phoneNumber: phoneValidation.formatted!,
        password: data.password,
        recaptchaToken: recaptcha.token,
      };
      await phoneLoginMutation.mutateAsync(submitData);
    } catch (err: any) {
      console.error('Phone login error:', err);
      setError(err.message || 'Login failed');

      // Reset reCAPTCHA on error
      if (recaptchaEnabled) {
        recaptcha.reset();
      }
    }
  };

  const isLoading = emailLoginMutation.isLoading || phoneLoginMutation.isLoading;

  const handlePhoneFlowSuccess = async (phoneNumber: string) => {
    try {
      // The phone number has been verified, now complete the login
      await phoneLoginMutation.mutateAsync({
        phoneNumber,
        password: '' // For SMS-only auth, password might not be needed
      });
    } catch (err: any) {
      console.error('Phone flow login error:', err);
      setError(err.message || 'Login failed');
    }
  };

  const handlePhoneFlowError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleGoogleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  return (
    <Layout showNavigation={false}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
          px: 2,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Welcome Back
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Sign in to manage your BnB properties
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Google Sign-In Button */}
          <GoogleSignInButton
            variant="sign-in"
            onError={handleGoogleError}
            disabled={isLoading}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              or
            </Typography>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>

          <Tabs
            value={loginMethod}
            onChange={(e, newValue) => setLoginMethod(newValue)}
            sx={{ mb: 3 }}
          >
            <Tab label="Email" value="email" />
            <Tab label="Phone" value="phone" />
          </Tabs>

          {loginMethod === 'email' && (
            <Box component="form" onSubmit={handleEmailSubmit(onEmailSubmit)} noValidate>
              <TextField
                {...registerEmail('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address'
                  }
                })}
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                error={!!emailErrors.email}
                helperText={emailErrors.email?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...registerEmail('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                })}
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                error={!!emailErrors.password}
                helperText={emailErrors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* reCAPTCHA */}
              {recaptchaEnabled && recaptchaSiteKey && (
                <Box sx={{ mt: 2 }}>
                  <ReCaptcha
                    siteKey={recaptchaSiteKey}
                    action="LOGIN"
                    onVerify={recaptcha.handleVerify}
                    onError={recaptcha.handleError}
                    disabled={isLoading}
                    autoExecute={true}
                  />
                  {recaptcha.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {recaptcha.error}
                    </Alert>
                  )}
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || (recaptchaEnabled && !recaptcha.isVerified)}
              >
                {emailLoginMutation.isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          )}

          {loginMethod === 'phone' && (
            <Box component="form" onSubmit={handlePhoneSubmit(onPhoneSubmit)} noValidate>
              <TextField
                {...registerPhone('phoneNumber', {
                  required: 'Phone number is required',
                  validate: (value) => {
                    const validation = validatePhoneNumber(value);
                    return validation.isValid || validation.error || 'Invalid phone number';
                  }
                })}
                margin="normal"
                required
                fullWidth
                id="phoneNumber"
                label="Phone Number"
                name="phoneNumber"
                autoComplete="tel"
                autoFocus
                error={!!phoneErrors.phoneNumber}
                helperText={phoneErrors.phoneNumber?.message || 'Enter with country code (e.g., +1 for US)'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...registerPhone('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                })}
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                error={!!phoneErrors.password}
                helperText={phoneErrors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* reCAPTCHA for phone login */}
              {recaptchaEnabled && recaptchaSiteKey && (
                <Box sx={{ mt: 2 }}>
                  <ReCaptcha
                    siteKey={recaptchaSiteKey}
                    action="LOGIN"
                    onVerify={recaptcha.handleVerify}
                    onError={recaptcha.handleError}
                    disabled={isLoading}
                    autoExecute={true}
                  />
                  {recaptcha.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {recaptcha.error}
                    </Alert>
                  )}
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || (recaptchaEnabled && !recaptcha.isVerified)}
              >
                {phoneLoginMutation.isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <MuiLink component={Link} to="/auth/forgot-password" variant="body2" sx={{ textDecoration: 'none' }}>
              Forgot your password?
            </MuiLink>
          </Box>

          {/* Enhanced Phone Authentication Flow */}
          {!showPhoneFlow && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setShowPhoneFlow(true)}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
              >
                Use SMS-only authentication
              </Button>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <MuiLink component={Link} to="/auth/register" sx={{ textDecoration: 'none' }}>
                Sign up here
              </MuiLink>
            </Typography>
          </Box>
        </Paper>

        {/* Enhanced Phone Authentication Flow */}
        {showPhoneFlow && (
          <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', mt: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom align="center">
              SMS Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Sign in using only your phone number with SMS verification
            </Typography>

            <PhoneAuthFlow
              mode="login"
              onSuccess={handlePhoneFlowSuccess}
              onError={handlePhoneFlowError}
              isLoading={isLoading}
              disabled={isLoading}
            />

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setShowPhoneFlow(false)}
                disabled={isLoading}
                color="inherit"
              >
                Back to regular login
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Layout>
  );
}